import seoRoutes from "../config/seoRoutes.json";
import {appLanguage, WEBSITE_TYPE} from "../config/Constants";

// /studentenkorting/:city_name/:name/:id and /cashback/studentenkorting/:name/:id (plus English variants)
const DETAIL_PATH = /^\/((studentenkorting|student-discounts)\/[^/]+|cashback\/(studentenkorting|student-discounts))\/[^/]+\/[^/]+$/;

// The nl/com build is shared, so the site is determined by the hostname and not by the build.
export function detectSite(hostname = window.location.hostname) {
    const host = hostname.toLowerCase();
    if (host.includes("knaek.be")) return appLanguage.be;
    if (host.includes("knaek.com")) return appLanguage.en;
    if (host.includes("knaek.nl")) return appLanguage.nl;
    return WEBSITE_TYPE === appLanguage.be ? appLanguage.be : appLanguage.nl;
}

export function htmlLang(language) {
    return seoRoutes.htmlLang[language] || seoRoutes.htmlLang.nl;
}

// Keep in sync with slugify() in src/uitls/SitemapGen.js
export function slugify(str) {
    return encodeURIComponent(String(str).replace(/\s+|\//g, "-"));
}

export function normalizePath(pathname) {
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
    return seoRoutes.pathAliases[path] || path || "/";
}

// Translates a path between the Dutch (nl/be) and English (com) route names.
export function localizePath(path, site) {
    const toEnglish = site === appLanguage.en;
    for (const [nl, en] of seoRoutes.pathPairs) {
        const from = toEnglish ? nl : en;
        const to = toEnglish ? en : nl;
        if (path.toLowerCase() === from.toLowerCase() || path.toLowerCase().startsWith(from.toLowerCase() + "/")) {
            return to + path.slice(from.length);
        }
    }
    return path;
}

export function siteUrl(path, site) {
    return seoRoutes.origins[site] + localizePath(normalizePath(path), site);
}

export function isDetailPath(path) {
    return DETAIL_PATH.test(normalizePath(path));
}

function isStaticPage(path) {
    const dutchPath = localizePath(normalizePath(path), appLanguage.nl).toLowerCase();
    return seoRoutes.staticPages.some(page => page.toLowerCase() === dutchPath);
}

// Static pages exist on all three sites; other pages only have an nl <-> com counterpart.
// sitePaths ({nl, be, en} -> path) overrides that for a page whose path differs per site, such as
// a cashback store that has a different id on .be. Only the sites in sitePaths get a link then.
export function alternateLinks(path, site, sitePaths = null) {
    const paths = sitePaths || {};
    const sites = sitePaths
        ? [appLanguage.nl, appLanguage.be, appLanguage.en].filter(s => paths[s])
        : isStaticPage(path)
            ? [appLanguage.nl, appLanguage.be, appLanguage.en]
            : [appLanguage.nl, appLanguage.en];
    if (!sites.includes(site) || sites.length < 2) return [];

    const pathFor = s => paths[s] || path;
    const links = sites.map(s => ({hreflang: seoRoutes.hreflang[s], href: siteUrl(pathFor(s), s)}));
    if (sites.includes(appLanguage.en)) {
        links.push({hreflang: "x-default", href: siteUrl(pathFor(appLanguage.en), appLanguage.en)});
    }
    return links;
}

export const CASHBACK_PREFIX = "/cashback/studentenkorting/";

// pairs: [nlSuffix, beSuffix] from src/data/cashback-alternates.json (written by SitemapGen.js), where a
// suffix is "<slug>/<id>". Returns the per-site paths of the cashback store with this id on this site,
// or null when the store has no counterpart on the other country (then only nl <-> com applies).
// ownPath (built from the loaded store) is used for this site, so a renamed store keeps its canonical.
export function cashbackSitePaths(site, id, ownPath, pairs) {
    const onBe = site === appLanguage.be;
    const pair = (pairs || []).find(([nl, be]) => idOfSuffix(onBe ? be : nl) === String(id));
    if (!pair) return null;
    const otherPath = CASHBACK_PREFIX + (onBe ? pair[0] : pair[1]);
    const nlPath = onBe ? otherPath : ownPath;
    return {[appLanguage.nl]: nlPath, [appLanguage.be]: onBe ? ownPath : otherPath, [appLanguage.en]: nlPath};
}

function idOfSuffix(suffix) {
    return decodeURIComponent(suffix.slice(suffix.lastIndexOf("/") + 1));
}

let cashbackPairs;

// Loaded once and only on cashback detail pages; a failed load means "no counterparts".
export function loadCashbackPairs() {
    if (!cashbackPairs) {
        // A dynamic import gives the file its own content-hashed chunk: it stays out of the main bundle
        // and a CDN cannot serve a stale copy under a fixed URL (Cloudflare kept an old index.html
        // for /cashback-alternates.json on knaek.com).
        cashbackPairs = import("../data/cashback-alternates.json")
            .then(data => (data.default || data).pairs || [])
            .catch(() => []);
    }
    return cashbackPairs;
}

// Google shows roughly 155 characters of a meta description, so cut at a word boundary.
export function metaDescription(...parts) {
    const text = parts.filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
    if (text.length <= 155) return text;
    return text.slice(0, 152).replace(/\s+\S*$/, "") + "...";
}

// schema.org BreadcrumbList for the visible breadcrumbs. items: [{name, path}] with Dutch paths, which
// siteUrl() turns into the URL on this site; the last item is the page itself and gets no URL.
export function breadcrumbJsonLd(items, site) {
    return JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.name,
            ...(index < items.length - 1 ? {item: siteUrl(item.path, site)} : {}),
        })),
    });
}

export function organizationJsonLd(site, sameAs = []) {
    const origin = seoRoutes.origins[site];
    return JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Knaek",
        url: origin + "/",
        logo: origin + "/assets/images/logo-roze.png",
        ...(sameAs.length ? {sameAs} : {}),
    });
}
